import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Upload, Loader2, ArrowRight, Check, MapPin, Star } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { apiRequest, queryClient, getQueryFn } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { AppLogo, AppBrandingText } from "@/components/app-logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getAvatarSrc } from "@/components/user-avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { useActiveLocations } from "@/hooks/use-active-locations";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

interface DefaultAvatar {
  key: string;
  label: string;
  url: string;
}

const nameSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(100, "First name must be 100 characters or less"),
  lastName: z.string().min(1, "Last name is required").max(100, "Last name must be 100 characters or less"),
});

const usernameSchema = z.object({
  username: z.string()
    .min(3, "Username must be at least 3 characters")
    .max(50, "Username must be 50 characters or less")
    .regex(/^[a-zA-Z0-9_]+$/, "Username can only contain letters, numbers, and underscores"),
});

type NameFormData = z.infer<typeof nameSchema>;
type UsernameFormData = z.infer<typeof usernameSchema>;

function generateDefaultUsername(firstName: string, lastName: string): string {
  const first = firstName.trim().toLowerCase();
  const last = lastName.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!first || !last) return "";
  return `${first[0]}${last}`.replace(/[^a-z0-9_]/g, "");
}

export default function Onboarding() {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const [step, setStep] = useState<"name" | "username" | "location" | "avatar">("name");
  const [savedName, setSavedName] = useState<{ firstName: string; lastName: string } | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [selectedAvatarKey, setSelectedAvatarKey] = useState<string | null>(null);
  const [selectedLocationCodes, setSelectedLocationCodes] = useState<string[]>([]);
  const [selectedDefaultLocationCode, setSelectedDefaultLocationCode] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { locations: activeLocations, isLoading: locationsLoading } = useActiveLocations();

  const nameForm = useForm<NameFormData>({
    resolver: zodResolver(nameSchema),
    defaultValues: {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
    },
  });

  const usernameForm = useForm<UsernameFormData>({
    resolver: zodResolver(usernameSchema),
    defaultValues: {
      username: user?.username || "",
    },
  });

  const defaultsQuery = useQuery<{ defaults: DefaultAvatar[] }>({
    queryKey: ["/api/account/avatar/defaults"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const profileMutation = useMutation({
    mutationFn: async (data: { firstName?: string; lastName?: string; username?: string }) => {
      return apiRequest("PATCH", "/api/account/profile", data);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save profile",
        variant: "destructive",
      });
    },
  });

  const setDefaultAvatarMutation = useMutation({
    mutationFn: async (key: string) => {
      await apiRequest("PATCH", "/api/account/avatar", { type: "default", key });
      return key;
    },
    onSuccess: (selectedKey: string) => {
      setSelectedAvatarKey(selectedKey);
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update avatar", variant: "destructive" });
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      const csrfToken = document.cookie
        .split("; ")
        .find((c) => c.startsWith("XSRF-TOKEN="))
        ?.split("=")[1];
      const response = await fetch("/api/account/avatar/upload", {
        method: "POST",
        body: formData,
        credentials: "include",
        headers: csrfToken ? { "X-XSRF-TOKEN": csrfToken } : {},
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: "Upload failed" }));
        throw new Error(err.message);
      }
      return response.json();
    },
    onSuccess: () => {
      setSelectedAvatarKey(null);
    },
    onError: (error: Error) => {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    },
  });

  const compressImage = (file: File, maxDimension = 1024, quality = 0.85): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDimension || height > maxDimension) {
          const ratio = Math.min(maxDimension / width, maxDimension / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) { resolve(file); return; }
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) { resolve(file); return; }
            resolve(new File([blob], file.name, { type: "image/jpeg" }));
          },
          "image/jpeg",
          quality
        );
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast({ title: "Invalid file", description: "Please select a JPEG, PNG, or WebP image.", variant: "destructive" });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "File too large", description: "Maximum file size is 10MB.", variant: "destructive" });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setUploadPreview(reader.result as string);
    reader.readAsDataURL(file);

    setSelectedAvatarKey(null);

    try {
      const compressed = await compressImage(file);
      uploadMutation.mutate(compressed);
    } catch {
      uploadMutation.mutate(file);
    }
  };

  const onSubmitName = async (data: NameFormData) => {
    await profileMutation.mutateAsync({
      firstName: data.firstName,
      lastName: data.lastName,
    });
    setSavedName({ firstName: data.firstName, lastName: data.lastName });
    const defaultUsername = generateDefaultUsername(data.firstName, data.lastName);
    usernameForm.setValue("username", defaultUsername);
    setStep("username");
  };

  const locationMutation = useMutation({
    mutationFn: async ({ codes, defaultCode }: { codes: string[]; defaultCode: string | null }) => {
      return apiRequest("PUT", "/api/account/locations", {
        locationCodes: codes,
        defaultLocationCode: defaultCode,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save locations",
        variant: "destructive",
      });
    },
  });

  const onSubmitUsername = async (data: UsernameFormData) => {
    await profileMutation.mutateAsync({
      username: data.username,
    });
    setStep("location");
  };

  const onSubmitLocations = async () => {
    const effectiveDefault = selectedLocationCodes.length === 1
      ? selectedLocationCodes[0]
      : (selectedDefaultLocationCode && selectedLocationCodes.includes(selectedDefaultLocationCode)
          ? selectedDefaultLocationCode
          : null);
    await locationMutation.mutateAsync({ codes: selectedLocationCodes, defaultCode: effectiveDefault });
    setStep("avatar");
  };

  const handleFinish = async () => {
    // P23C: record the "onboarded" milestone so tour audience targeting
    // can use milestones.lacks("onboarded") to show welcome-only tours.
    await apiRequest("POST", "/api/milestones", {
      key: "onboarded",
      source: "system",
    }).catch(() => undefined); // best-effort; don't block navigation on failure
    await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    await refreshUser();
  };

  if (!user) return null;

  const displayName = savedName
    ? `${savedName.firstName} ${savedName.lastName}`
    : [user.firstName, user.lastName].filter(Boolean).join(" ") || user.username;
  const initials = displayName.split(/\s+/).map(p => p[0]).join("").toUpperCase().slice(0, 2) || "?";

  const currentAvatarSrc = uploadPreview
    ? uploadPreview
    : selectedAvatarKey
      ? defaultsQuery.data?.defaults.find(d => d.key === selectedAvatarKey)?.url || null
      : getAvatarSrc(user.avatarUrl, user.id);
  const isPending = setDefaultAvatarMutation.isPending || uploadMutation.isPending;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background px-4" data-testid="onboarding-page">
      <div className="max-w-lg w-full space-y-8">
        <div className="flex flex-col items-center gap-3">
          <AppLogo size="lg" />
          <AppBrandingText className="text-xl" />
        </div>

        <div className="flex items-center justify-center gap-2 mb-2">
          {(["name", "username", "location", "avatar"] as const).map((s, i) => {
            const stepOrder = ["name", "username", "location", "avatar"];
            const currentIndex = stepOrder.indexOf(step);
            const thisIndex = i;
            const isComplete = thisIndex < currentIndex;
            const isCurrent = s === step;
            return (
              <div key={s} className="flex items-center gap-2">
                {i > 0 && <div className={cn("h-px w-8", isComplete || isCurrent ? "bg-primary" : "bg-border")} />}
                <div
                  className={cn(
                    "h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors",
                    isComplete ? "bg-primary text-primary-foreground" :
                    isCurrent ? "bg-primary text-primary-foreground" :
                    "bg-muted text-muted-foreground"
                  )}
                  data-testid={`step-indicator-${s}`}
                >
                  {isComplete ? <Check className="h-4 w-4" /> : i + 1}
                </div>
              </div>
            );
          })}
        </div>

        {step === "name" && (
          <div className="space-y-6" data-testid="step-name">
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight" data-testid="text-onboarding-title">
                Welcome! Let's set up your profile
              </h1>
              <p className="text-muted-foreground">
                Start by entering your name so your team can recognize you.
              </p>
            </div>

            <Form {...nameForm}>
              <form onSubmit={nameForm.handleSubmit(onSubmitName)} className="space-y-4">
                <FormField
                  control={nameForm.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="First name"
                          autoFocus
                          data-testid="input-onboarding-first-name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={nameForm.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Last name"
                          data-testid="input-onboarding-last-name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full"
                  disabled={profileMutation.isPending}
                  data-testid="button-onboarding-next-name"
                >
                  {profileMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <ArrowRight className="h-4 w-4 mr-2" />
                  )}
                  Continue
                </Button>
              </form>
            </Form>
          </div>
        )}

        {step === "username" && (
          <div className="space-y-6" data-testid="step-username">
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                Choose your username
              </h1>
              <p className="text-muted-foreground">
                We've suggested one based on your name. Feel free to change it.
              </p>
            </div>

            <Form {...usernameForm}>
              <form onSubmit={usernameForm.handleSubmit(onSubmitUsername)} className="space-y-4">
                <FormField
                  control={usernameForm.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Username</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Username"
                          autoFocus
                          data-testid="input-onboarding-username"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full"
                  disabled={profileMutation.isPending}
                  data-testid="button-onboarding-next-username"
                >
                  {profileMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <ArrowRight className="h-4 w-4 mr-2" />
                  )}
                  Continue
                </Button>
              </form>
            </Form>
          </div>
        )}

        {step === "location" && (
          <div className="space-y-6" data-testid="step-location">
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                Where do you work?
              </h1>
              <p className="text-muted-foreground">
                Select all the shelter locations you work at. BETI uses this to show you the animals and activity most relevant to you.
              </p>
            </div>

            <div className="rounded-md border bg-muted/40 px-4 py-3 text-sm space-y-2">
              <div className="flex gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">Your locations</span> — the Animals and Interactions pages will pre-filter to show only data from your selected shelter locations.
                </p>
              </div>
              <div className="flex gap-2">
                <Star className="h-4 w-4 text-amber-500 fill-amber-500 shrink-0 mt-0.5" />
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">Default location</span> — if you select more than one, you can star your primary location. It will be pre-selected automatically each time you open those pages. Selecting just one location sets it as your default automatically.
                </p>
              </div>
            </div>

            {locationsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : activeLocations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm" data-testid="text-no-locations">
                No locations configured yet. You can set this later in Settings.
              </div>
            ) : (
              <div className="space-y-2">
                {activeLocations.map((loc) => {
                    const isSelected = selectedLocationCodes.includes(loc.code);
                    const isDefault = selectedDefaultLocationCode === loc.code;
                    const showStar = isSelected && selectedLocationCodes.length >= 2;
                    return (
                      <label
                        key={loc.code}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-colors",
                          isSelected ? "border-primary bg-primary/5" : "hover-elevate"
                        )}
                        data-testid={`checkbox-location-${loc.code}`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => {
                            setSelectedLocationCodes(prev =>
                              checked
                                ? [...prev, loc.code]
                                : prev.filter(c => c !== loc.code)
                            );
                            if (!checked) {
                              setSelectedDefaultLocationCode(prev => prev === loc.code ? null : prev);
                            }
                          }}
                        />
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{loc.label}</div>
                            <div className="text-xs text-muted-foreground">{loc.code}</div>
                          </div>
                        </div>
                        <div className="w-6 flex justify-center shrink-0">
                          {showStar && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedDefaultLocationCode(prev => prev === loc.code ? null : loc.code);
                              }}
                              data-testid={`button-star-location-${loc.code}`}
                              className="p-0.5 rounded text-muted-foreground hover:text-amber-500 transition-colors"
                            >
                              <Star className={`h-3.5 w-3.5 ${isDefault ? "text-amber-500 fill-amber-500" : ""}`} />
                            </button>
                          )}
                        </div>
                      </label>
                    );
                  })}
              </div>
            )}

            <Button
              className="w-full"
              onClick={onSubmitLocations}
              disabled={locationMutation.isPending}
              data-testid="button-onboarding-next-location"
            >
              {locationMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4 mr-2" />
              )}
              {selectedLocationCodes.length === 0 ? "Skip" : "Continue"}
            </Button>
          </div>
        )}

        {step === "avatar" && (
          <div className="space-y-6" data-testid="step-avatar">
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                Pick a profile photo
              </h1>
              <p className="text-muted-foreground">
                Choose an avatar or upload your own picture.
              </p>
            </div>

            <div className="flex justify-center">
              <Avatar className="h-20 w-20">
                {currentAvatarSrc && (
                  <AvatarImage src={currentAvatarSrc} alt={displayName} />
                )}
                <AvatarFallback className="text-2xl">{initials}</AvatarFallback>
              </Avatar>
            </div>

            <div>
              <p className="text-sm font-medium mb-3">Default Avatars</p>
              <div className="grid grid-cols-5 gap-3 max-h-[280px] overflow-y-auto">
                {defaultsQuery.data?.defaults.map((avatar) => {
                  const isSelected = selectedAvatarKey === avatar.key || (!selectedAvatarKey && !uploadPreview && user.avatarUrl === `default:${avatar.key}`);
                  return (
                    <button
                      key={avatar.key}
                      onClick={() => {
                        setUploadPreview(null);
                        setDefaultAvatarMutation.mutate(avatar.key);
                      }}
                      disabled={isPending}
                      className={cn(
                        "flex flex-col items-center gap-1 p-2 rounded-md transition-colors",
                        isSelected
                          ? "ring-2 ring-primary bg-primary/5"
                          : "hover-elevate"
                      )}
                      data-testid={`button-onboarding-avatar-${avatar.key}`}
                    >
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={avatar.url} alt={avatar.label} />
                        <AvatarFallback>{avatar.label[0]}</AvatarFallback>
                      </Avatar>
                      <span className="text-xs text-muted-foreground truncate w-full text-center">{avatar.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-2">Upload Custom Photo</p>
              <p className="text-xs text-muted-foreground mb-3">JPEG, PNG, or WebP. Max 10MB.</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="input-onboarding-avatar-upload"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={isPending}
                data-testid="button-onboarding-upload-avatar"
              >
                {uploadMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Choose File
                  </>
                )}
              </Button>
            </div>

            <Button
              className="w-full"
              onClick={handleFinish}
              disabled={isPending}
              data-testid="button-onboarding-finish"
            >
              <Check className="h-4 w-4 mr-2" />
              Get Started
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
